import assertNever from "assert-never";
import * as React from "react";
import { API } from "../../API";
import { openExternalUrl } from "../../external_link";
import useMixin from "../../hooks/useMixin";
import useShare from "../../hooks/useShare";
import useTrainingCenterState from "../../hooks/useTrainingCenterState";
import { isLoading } from "../../Loading";
import { Agency } from "../../messages";
import { TrainingCenterBundleDueDateId } from "../../messages/ObjectIds";
import { TrainingCenterDisplayLanguage } from "../../messages/TrainingCenter";
import { retryableModalNetworkRequest } from "../../NetworkRequest";
import { STRINGS } from "../../strings";
import { AppState } from "../../system/AppState";
import {
  TrainingCenterBundleItem,
  TrainingCenterCaregiverBundle,
} from "../../system/AppState/AppStateTrainingCenter";
import { translate } from "../../translate";
import { List } from "../../utils/List";
import { isDefined } from "../Generic/IsDefined";
import LoadingScaffold from "../LoadingScaffold";
import Scaffold from "../Scaffold/Scaffold";
import ScreenPlaceholder from "../ScreenPlaceholder";
import { TrainingCenterLanguagePreference } from "../TrainingCenter/TrainingCenterLanguageLookup";
import TrainingCenterBundleScreen from "./TrainingCenterBundleScreen";

interface TrainingCenterBundleActivityProps {
  api: API;
  appState: AppState;
  languagePreference: TrainingCenterLanguagePreference;
  bundleDueDateId: TrainingCenterBundleDueDateId;
  allLanguages: List<TrainingCenterDisplayLanguage>;

  onLanguagePreferenceChange: (languagePreference: TrainingCenterLanguagePreference) => void;
  onRequestNavigateToBundleItem: (
    bundleItem: TrainingCenterBundleItem,
    bundle: TrainingCenterCaregiverBundle
  ) => void;
  onBundleNotFound: () => void;
  onClickGoBack: () => void;
  onDragDown: () => void;
}

type State =
  | { type: "Loading" }
  | { type: "Error"; errorTitle: string; errorMessage: string }
  | { type: "Success"; bundle: TrainingCenterCaregiverBundle; agency: Agency };

const TrainingCenterBundleActivity = (props: TrainingCenterBundleActivityProps) => {
  useTrainingCenterState(props.appState);

  const [state, setState] = React.useState<State>({ type: "Loading" });
  const mixin = useMixin(props.appState);
  const { share, canShare } = useShare();

  const handleClickOpenCertificate = async () => {
    const result = await retryableModalNetworkRequest(
      () => props.api.getTrainingCenterBundleCertificatePdf(props.bundleDueDateId),
      true
    );
    if (result !== null) {
      openExternalUrl(result.url);
    }
  };

  const handleClickShareCertificate = async () => {
    const result = await retryableModalNetworkRequest(
      () => props.api.getTrainingCenterBundleCertificatePdf(props.bundleDueDateId),
      true
    );
    if (result !== null) {
      share({ url: result.url });
    }
  };

  const handleClickPlayBundle = (bundle: TrainingCenterCaregiverBundle) => {
    const bundleItem = bundle.bundleItems.find((x) => !x.itemCompleted);

    if (bundleItem !== undefined) {
      return props.onRequestNavigateToBundleItem(bundleItem, bundle);
    }
  };

  React.useEffect(() => {
    if (isLoading(props.appState.appTrainingCenterState) || isLoading(mixin.appAgenciesState)) {
      return;
    }

    const bundle = props.appState.appTrainingCenterState.bundles.find(
      (b) => b.bundleDueDateId === props.bundleDueDateId
    );

    if (!isDefined(bundle)) {
      return setState({
        type: "Error",
        errorTitle: translate(STRINGS.NOT_FOUND),
        errorMessage: translate(STRINGS.BUNDLE_NOT_FOUND),
      });
    }

    const agency = lookupWithDefault(
      mixin.appAgenciesState.agencies.get(bundle.agencyId),
      (a) => a.agency,
      undefined
    );

    if (!isDefined(agency)) {
      return setState({
        type: "Error",
        errorTitle: translate(STRINGS.NOT_FOUND),
        errorMessage: translate(STRINGS.AGENCY_NOT_FOUND),
      });
    }

    setState({ type: "Success", bundle: bundle, agency: agency });
  }, [mixin.appAgenciesState, props.appState.appTrainingCenterState, props.bundleDueDateId]);

  switch (state.type) {
    case "Loading":
      return <LoadingScaffold onClickGoBack={props.onClickGoBack} />;
    case "Error":
      return (
        <Scaffold title={state.errorTitle} onClickGoBack={props.onClickGoBack}>
          <ScreenPlaceholder
            headline={state.errorMessage}
            buttonText={translate(STRINGS.GO_BACK)}
            onClick={props.onClickGoBack}
          />
        </Scaffold>
      );
    case "Success":
      return (
        <TrainingCenterBundleScreen
          languagePreference={props.languagePreference}
          bundle={state.bundle}
          canSocialShare={canShare}
          onClickBundleItem={props.onRequestNavigateToBundleItem}
          onClickOpenCertificate={handleClickOpenCertificate}
          onClickShareCertificate={handleClickShareCertificate}
          onClickPlayBundle={handleClickPlayBundle}
          onClickGoBack={props.onClickGoBack}
          onDragDown={props.onDragDown}
        />
      );
    default:
      assertNever(state);
  }
};

function lookupWithDefault<T, S>(val: T | undefined, lookup: (val: T) => S, def: S): S {
  if (val === undefined) {
    return def;
  } else {
    return lookup(val);
  }
}

export default React.memo(TrainingCenterBundleActivity);
